import { useId, useState } from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import { useCurrentUserContext } from '@/contexts';
import { useImagePicker } from '@/features/common/components/ImagePicker/useImagePicker';
import { TextAreaCharacterCount } from '@/pages/Schedule/components/TextAreaCharacterCount';
import type { QuickEntriesJobFile } from '@/types/gql.generated';
import { Flex, Button, Text, Textarea, useDisclosure } from '@/ui';
import { FilePreview } from '../FilePreview';
import { ExamplesModal } from './ExamplesModal';

type Props = {
  file: File | QuickEntriesJobFile;
  instructions: string;
  isDisabled?: boolean;
  inputId: string;
  onInstructionsChange: (instructions: string) => void;
  onFileChange: (file: File) => void;
};

const MAX_CHARACTER_LIMIT = 750;

export const UploadPreview = ({
  file,
  instructions,
  isDisabled,
  inputId,
  onInstructionsChange,
  onFileChange,
}: Props) => {
  const [characterCount, setCharacterCount] = useState(0);

  const { open: openImagePicker } = useImagePicker();
  const { isAuthenticated } = useCurrentUserContext();
  const textareaId = useId();
  const { isOpen, onClose: close, onOpen: open } = useDisclosure();

  const handleTextareaChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setCharacterCount(event.target.value.length);
    onInstructionsChange(event.target.value);
  };

  return (
    <Flex
      bg="rgba(255, 255, 255, 0.5)"
      boxSize="100%"
      direction={{ base: 'column', sm: 'row' }}
      justify="center"
    >
      <ExamplesModal isOpen={isOpen} onClose={close} />

      <Flex
        align="center"
        borderBottomWidth={{ base: 1, sm: 0 }}
        borderColor="gray.200"
        borderRightWidth={{ sm: 1 }}
        direction="column"
        flexBasis="35%"
        gap="3"
        px="6"
        py="4"
      >
        <FilePreview
          file={file}
          imageZoomEnabled={false}
          maxImageHeight={175}
          maxImageWidth={120}
          pdfZoomEnabled={false}
        />

        {!isDisabled && (
          <>
            {isAuthenticated ? (
              <Button
                colorScheme="dark"
                textDecor="underline"
                variant="link"
                onClick={() => openImagePicker({ onSelect: onFileChange })}
              >
                Replace
              </Button>
            ) : (
              <Button
                as="label"
                colorScheme="dark"
                cursor="pointer"
                htmlFor={inputId}
                textDecor="underline"
                variant="link"
              >
                Replace
              </Button>
            )}
          </>
        )}
      </Flex>

      <Flex
        align="flex-start"
        direction="column"
        flex="1"
        fontSize="sm"
        gap="1.5"
        justifyItems="start"
        px="6"
        py="4"
      >
        <Text as="label" fontWeight="bold" htmlFor={textareaId}>
          Optional: add some additional context
        </Text>

        <Button
          colorScheme="dark"
          textDecor="underline"
          variant="link"
          onClick={open}
        >
          Helpful tips
        </Button>

        <Textarea
          as={TextareaAutosize}
          bg="white"
          fontSize="sm"
          id={textareaId}
          isDisabled={isDisabled}
          maxLength={MAX_CHARACTER_LIMIT}
          mt="1"
          placeholder='Try "This event repeats every week"'
          value={instructions}
          onChange={handleTextareaChange}
        />
        <TextAreaCharacterCount
          maxLength={MAX_CHARACTER_LIMIT}
          value={characterCount}
        />
      </Flex>
    </Flex>
  );
};
